import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingComp } from "../../../../../shared/components/loading/Loading";
import { VForm, useVForm } from "../../../../../shared/forms";
import { FireServices } from "../../../../../shared/services/api/fire/FireServices";
import { AssistanceItemData, ClientFireSettingsPayload, TokioMarineSettingsFormProps } from "../../../../../shared/services/api/fire/types";
import { globalRemoveMaskReal2 } from "../../../../../shared/utils";
import { TokioAssistanceManager } from "../forms/TokioAssistanceManager";
import { TokioBaseSettings } from "../forms/TokioBaseSettings";
import { TokioCoverageManager } from "../forms/TokioCoverageManager";
import { useTokioCoverage } from "../hooks/useTokioCoverage";
import { useTokioData } from "../hooks/useTokioData";
import { useToast } from "../../../../../shared/hooks/useToast";

type PropertyType = "residential" | "commercial";

const TokioMarineManagerFormComponent: React.FC<TokioMarineSettingsFormProps> = ({ clientId, insurerId, onBack, onNext, shouldLoad = false }) => {
	const { formRef } = useVForm();
	const toastHandler = useToast();
	const [assistanceTipo, setAssistanceTipo] = useState<PropertyType>("residential");
	const [coverageTipo, setCoverageTipo] = useState<PropertyType>("commercial");
	const [expandedCoverage, setExpandedCoverage] = useState<string | false>(false);
	const [isFormDirty, setIsFormDirty] = useState(false);
	const [refetchKey, setRefetchKey] = useState(0);

	const {
		isLoading,
		setIsLoading,
		initialData,
		assistancesResidential,
		assistancesCommercial,
		initialCoverages,
		setAssistancesResidential,
		setAssistancesCommercial,
		hasLoadError,
	} = useTokioData({ clientId, insurerId, page: 1, limit: 50, shouldLoad, refetchKey });

	const { coverageType, setCoverageType, showOutras, handlePredialBlur } = useTokioCoverage(formRef);

	useEffect(() => {
		if (initialData?.coverageType) {
			setCoverageType(initialData.coverageType);
		}
	}, [initialData, setCoverageType]);

	const handleCoverageChange = useCallback(
		(panelKey: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
			setExpandedCoverage(newExpanded ? panelKey : false);
		},
		[]
	);

	const handleBaseFormChange = useCallback(() => {
		if (!isFormDirty) {
			setIsFormDirty(true);
		}
	}, [isFormDirty]);

	const handleRefetchData = useCallback(() => {
		setRefetchKey((prev) => prev + 1);
	}, [setRefetchKey]);

	const handleLoadDefaultTemplate = useCallback(() => {
		setIsLoading(true);

		FireServices.getTemplateTokioSettings(clientId, insurerId).then((result) => {
			if (result instanceof Error) {
				console.error("Erro ao buscar template de configurações Tokio:", result);
				toastHandler.present({
					type: "error",
					messageOrContent: result.message || "Falha ao carregar o template padrão. Verifique a API.",
					position: "top-right",
				});

				setIsLoading(false);
				return;
			}

			const templateData = result.data;

			formRef.current?.setData(templateData);

			if (templateData?.coverageType) {
				setCoverageType(templateData.coverageType);
			}

			setIsFormDirty(true);
			console.log("Template padrão carregado com sucesso.");
			setIsLoading(false);

			setRefetchKey((prev) => prev + 1);
		});
	}, [clientId, insurerId, setIsLoading, formRef, setCoverageType, setIsFormDirty, toastHandler]);

	const handleSubmit = useCallback(
		async (data: any) => {
			if (!isFormDirty) {
				console.log("Nenhuma alteração detectada para salvar.");
				return;
			}

			setIsLoading(true);

			try {
				const finalPayload: ClientFireSettingsPayload = {
					clientId,
					insurance_companyId: insurerId,
					installment_number: Number(data.installment_number || 1),
					pc_predio: globalRemoveMaskReal2(data.pc_predio ?? "0").trim(),
					pc_conteudo: globalRemoveMaskReal2(data.pc_conteudo ?? "0").trim(),
					user_insurance_companies: (data.user_insurance_companies ?? "").trim(),
					password_insurance_companies: (data.password_insurance_companies ?? "").trim(),
					integrator_code: (data.integrator_code ?? "").trim(),
					transfer_type: (data.transfer_type ?? "broker").trim(),
					comission_insurance_companies: globalRemoveMaskReal2(data.comission_insurance_companies ?? "0").trim(),
					comission_broker: globalRemoveMaskReal2(data.comission_broker ?? "0").trim(),
					status: "active",
					close_data: Number(data.close_data || 0),
					due_date: Number(data.due_date || 0),
				};

				const result = await FireServices.updateClientFireSettings(finalPayload as unknown as ClientFireSettingsPayload);

				if (result instanceof Error) {
					throw result;
				}

				setRefetchKey((prev) => prev + 1);
				setIsFormDirty(false);

				// Feedback de sucesso
				toastHandler.present({
					type: "success",
					messageOrContent: "Configurações salvas com sucesso!",
					position: "top-right",
				});

				onNext?.();

			} catch (error: any) {
				console.error("Erro ao salvar configurações:", error);
				toastHandler.present({
					type: "error",
					messageOrContent: error.message || "Falha ao salvar as configurações. Verifique o console.",
					position: "top-right",
				});
			} finally {
				setIsLoading(false);
			}
		},
		[setIsLoading, onNext, isFormDirty, clientId, insurerId, setRefetchKey, toastHandler]
	);

	const visibleAssistances = assistanceTipo === "residential" ? assistancesResidential : assistancesCommercial;

	const setVisibleAssistances = (next: AssistanceItemData[]) => {
		if (assistanceTipo === "residential") setAssistancesResidential(next);
		else setAssistancesCommercial(next);
	};

	const setAssistanceTipoAndDirty = (v: PropertyType) => {
		setAssistanceTipo(v);
		setIsFormDirty(true);
	};

	const setCoverageTipoAndDirty = (v: PropertyType) => {
		setCoverageTipo(v);
		setIsFormDirty(true);
	};

	// CORREÇÃO DO FILTRO DE COBERTURAS (MANTIDO EM useMemo)
	const filteredCoverages = useMemo(() => {
		return initialCoverages.filter((group) => group.category === coverageTipo);
	}, [initialCoverages, coverageTipo]);

	const content = useMemo(() => {
		if (!initialData && !shouldLoad) return null;

		if (isLoading)
			return (
				<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
					<Typography color="textSecondary">Carregando configurações da Seguradora...</Typography>
				</Box>
			);

		if (!initialData && !hasLoadError)
			return (
				<Box sx={{ p: 4 }}>
					<Alert severity="warning">
						<Typography variant="body1">
							**Configurações não localizadas** para esta seguradora e cliente. É necessário carregar um template padrão antes de salvar as
							configurações.
						</Typography>
					</Alert>

					<Box mt={2} display="flex" justifyContent="flex-start">
						<Button variant="contained" color="primary" onClick={handleLoadDefaultTemplate} disabled={isLoading}>
							Carregar Template Padrão
						</Button>
					</Box>

					<Box mt={2}>
						<Typography variant="body2" color="textSecondary">
							*Se o carregamento do template falhar, verifique a conexão com a API ou se o template está disponível.*
						</Typography>
					</Box>
				</Box>
			);

		if (!initialData && hasLoadError) {
			return null;
		}

		return (
			<>
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>
						Configurações Tokio Marine
					</Typography>
					<Button
						variant="contained"
						color={isFormDirty ? "secondary" : "primary"}
						onClick={() => formRef.current?.submitForm()}
						disabled={isLoading || !isFormDirty}
					>
						Salvar / Atualizar
					</Button>
				</Box>

				<VForm
					key={refetchKey}
					ref={formRef}
					onSubmit={handleSubmit}
					initialData={initialData ?? undefined}
					onChange={handleBaseFormChange}
					onPointerEnterCapture={() => { }}
					onPointerLeaveCapture={() => { }}
					placeholder={" "}
				>
					<TokioBaseSettings
						isLoading={isLoading}
						coverageType={coverageType}
						setCoverageType={(type) => {
							setCoverageType(type);
							setIsFormDirty(true);
						}}
						showOutras={showOutras}
						handlePredialBlur={handlePredialBlur}
						formRef={formRef}
						onFormChange={handleBaseFormChange}
					/>
				</VForm>

				<Divider sx={{ my: 2 }} />

				<TokioAssistanceManager
					isLoading={isLoading}
					tipo={assistanceTipo}
					setTipo={setAssistanceTipoAndDirty}
					visibleAssistances={visibleAssistances}
					setVisibleAssistances={setVisibleAssistances}
				/>

				<Divider sx={{ my: 2 }} />

				{/* O TokioCoverageManager agora receberá o handler de refetch */}
				<TokioCoverageManager
					isLoading={isLoading}
					coverageTipo={coverageTipo}
					setCoverageTipoAndDirty={setCoverageTipoAndDirty}
					filteredCoverages={filteredCoverages}
					expandedCoverage={expandedCoverage}
					handleCoverageChange={handleCoverageChange}
					onSuccessfulUpdate={handleRefetchData}
				/>
			</>
		);
	}, [
		formRef,
		isLoading,
		initialData,
		shouldLoad,
		coverageType,
		showOutras,
		setCoverageType,
		handlePredialBlur,
		assistanceTipo,
		coverageTipo,
		expandedCoverage,
		handleCoverageChange,
		visibleAssistances,
		filteredCoverages,
		handleSubmit,
		handleRefetchData,
		setAssistanceTipoAndDirty,
		setCoverageTipoAndDirty,
		setVisibleAssistances,
		handleBaseFormChange,
		isFormDirty,
		handleLoadDefaultTemplate,
		refetchKey,
		hasLoadError,
	]);

	return (
		<>
			<LoadingComp isopen={isLoading} />
			{content}
		</>
	);
};

export const TokioMarineManagerForm = React.memo(TokioMarineManagerFormComponent);
